import { useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { Page } from "v2/components/Page";
import { formatCnpj } from "utils/federal-tax-id";

import { fraseDoEvento, juntarConfirmacoes, TEXTOS } from "./frases";
import { gestaoDeUsuarios } from "./gestao-de-usuarios.service";
import { IClienteDaCarteira, IEuNaGestao } from "./gestao-de-usuarios.types";
import { TabelaDeUsuarios } from "./TabelaDeUsuarios";

// why: F3 (spec v1.7 §3.4, §9.2; plano 11 T8). Configurações > Gestão de usuários. Quem pode o
// quê decide o backend (/v2/acesso); a tela só esconde o que ele recusaria.

const POR_PAGINA = 50;

export function Historico({ organizationId }: { organizationId: string }) {
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["acesso", "eventos", organizationId],
    queryFn: ({ pageParam }) => gestaoDeUsuarios.eventos(organizationId, pageParam),
    initialPageParam: undefined as { antesDe: string; antesDeId: string } | undefined,
    getNextPageParam: (ultima) => {
      const fim = ultima[ultima.length - 1];
      return ultima.length === POR_PAGINA && fim ? { antesDe: fim.criadoEm, antesDeId: fim.id } : undefined;
    },
    gcTime: 0,
  });
  if (isLoading) return <CircularProgress />;
  if (isError || !data) return <Alert severity="error">{TEXTOS.ERRO_AO_CARREGAR}</Alert>;
  const eventos = juntarConfirmacoes(data.pages.flat());
  if (eventos.length === 0) return <Typography variant="body2">{TEXTOS.SEM_EVENTOS}</Typography>;
  return (
    <Box>
      <List dense>
        {eventos.map((e) => (
          <ListItem key={e.id} disableGutters>
            <ListItemText primary={fraseDoEvento(e)} secondary={new Date(e.criadoEm).toLocaleString("pt-BR")} />
          </ListItem>
        ))}
      </List>
      {hasNextPage && (
        <Button disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
          Ver mais antigos
        </Button>
      )}
    </Box>
  );
}

function Equipe({ eu }: { eu: IEuNaGestao }) {
  const { organizacao } = eu;
  return (
    <TabelaDeUsuarios
      organizationId={organizacao.id}
      organizacaoNome={organizacao.nome}
      lado={organizacao.lado ?? "EMPRESA"}
      editavel={eu.podeGerir}
      podeGerir={eu.podeGerir}
      podeRevisar={eu.podeRevisar}
      aviso={eu.podeGerir || organizacao.revisada ? undefined : TEXTOS.NAO_REVISADA}
    />
  );
}

function avisoDoCliente(c: IClienteDaCarteira) {
  if (c.temGestaoPropria) return TEXTOS.GESTAO_PROPRIA(c.nome);
  return c.revisada ? undefined : TEXTOS.NAO_REVISADA;
}

function Clientes() {
  const [aberto, setAberto] = useState<IClienteDaCarteira | null>(null);
  const { data: carteira, isLoading, isError } = useQuery({
    queryKey: ["acesso", "carteira"],
    queryFn: gestaoDeUsuarios.carteira,
    gcTime: 0,
  });
  if (isLoading) return <CircularProgress />;
  if (isError || !carteira) return <Alert severity="error">{TEXTOS.ERRO_AO_CARREGAR}</Alert>;

  // why: depois de uma ação, a carteira recarrega; o cliente aberto vem dela, não do clique antigo.
  const atual = aberto && carteira.find((c) => c.organizationId === aberto.organizationId);
  if (atual)
    return (
      <Box>
        <Button onClick={() => setAberto(null)} sx={{ mb: 1 }}>
          Voltar aos clientes
        </Button>
        <Typography variant="h6" gutterBottom>
          {atual.nome}
        </Typography>
        <TabelaDeUsuarios
          organizationId={atual.organizationId}
          organizacaoNome={atual.nome}
          lado="EMPRESA"
          editavel={atual.revisada && !atual.temGestaoPropria}
          podeGerir={false}
          podeRevisar={!atual.temGestaoPropria}
          aviso={avisoDoCliente(atual)}
        />
        <Typography variant="subtitle1" sx={{ mt: 3 }}>
          Histórico
        </Typography>
        <Historico organizationId={atual.organizationId} />
      </Box>
    );

  return (
    <Table size="small" aria-label="Clientes da carteira">
      <TableHead>
        <TableRow>
          <TableCell>Cliente</TableCell>
          <TableCell>CNPJ</TableCell>
          <TableCell>Administradores</TableCell>
          <TableCell>Quem administra</TableCell>
          <TableCell>Usuários</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {carteira.map((c) => (
          <TableRow key={c.organizationId} hover sx={{ cursor: "pointer" }} onClick={() => setAberto(c)}>
            <TableCell>{c.nome}</TableCell>
            <TableCell>{formatCnpj(c.cnpj)}</TableCell>
            <TableCell>
              <Chip
                size="small"
                color={c.revisada ? "success" : "default"}
                label={c.revisada ? TEXTOS.REVISADA : TEXTOS.NAO_REVISADA_CURTO}
              />
            </TableCell>
            <TableCell>{c.temGestaoPropria ? TEXTOS.ADMINISTRADOR_PROPRIO : TEXTOS.CORRETORA_ADMINISTRA}</TableCell>
            <TableCell>{c.usuarios}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function GestaoDeUsuarios() {
  // why: gcTime 0 em toda consulta da tela: trocar de organização sai da tela (/redirect) e a
  // volta não pode mostrar a equipe e os botões da organização anterior.
  const { data: eu, isLoading, isError } = useQuery({
    queryKey: ["acesso", "eu"],
    queryFn: gestaoDeUsuarios.eu,
    gcTime: 0,
  });
  if (isLoading)
    return (
      <Page title={TEXTOS.TITULO}>
        <Page.Tab name="Equipe">
          <CircularProgress />
        </Page.Tab>
      </Page>
    );
  if (isError || !eu)
    return (
      <Page title={TEXTOS.TITULO}>
        <Page.Tab name="Equipe">
          <Alert severity="error">{TEXTOS.ERRO_AO_CARREGAR}</Alert>
        </Page.Tab>
      </Page>
    );
  if (!eu.podeVer)
    return (
      <Page title={TEXTOS.TITULO}>
        <Page.Tab name="Equipe">
          <Alert severity="info">{TEXTOS.SEM_ACESSO(eu.organizacao.nome)}</Alert>
        </Page.Tab>
      </Page>
    );
  return (
    <Page title={TEXTOS.TITULO}>
      <Page.Tab name="Equipe">
        <Equipe eu={eu} />
      </Page.Tab>
      {eu.organizacao.lado === "CORRETORA" ? (
        <Page.Tab name="Clientes">
          <Clientes />
        </Page.Tab>
      ) : null}
      <Page.Tab name="Histórico">
        <Historico organizationId={eu.organizacao.id} />
      </Page.Tab>
    </Page>
  );
}
